/**
 * Rating Picker UI Component
 *
 * @example <sc-review-rating-selector ng-model="vm.model" min="vm.min" max="vm.max" step="vm.step">
 *          </sc-review-rating-selector>
 * @description Component of Sandcastle Framework. Inserts a rating picker UI component.
 * @namespace Core
 * @memberof TR
 */

'use strict';

namespace Core {

    class ScReviewRatingSelector implements ng.IDirective {

        restrict: string;
        prefix: string;
        replace: boolean;
        transclude: boolean;
        require: string;
        scope: {};
        getRandomID;
        template;

        constructor() {

            this.restrict = 'E';
            this.replace = true;
            this.require = 'ngModel';
            this.scope = {
                caption: '@',
                model: '=ngModel',
                max: '@',
                min: '@',
                step: '@',
                size: '@'
            };

            this.template =  `<div class="sc-c-range-bar"
                                 ng-class="containerClasses">
                                <label class="sc-c-range-bar__label" for="{{componentId}}">{{::caption}}</label>
                                <div class="sc-c-range-bar__block-wrapper">
                                    <input class="sc-c-range-bar__range"
                                           id="{{componentId}}"
                                           min="{{min}}"
                                           max="{{max}}"
                                           step="{{step}}"
                                           type="range" 
                                           ng-model="model" 
                                    />
                                    <label class="sc-c-range-bar__block"
                                          for="{{componentId}}"
                                          ng-class="{'sc-c-range-bar__block--selected' : r <= model}"
                                          ng-click="click.setValue(r)"
                                          ng-repeat="r in range">
                                    </label>
                                </div>
                                <div class="sc-c-range-bar__rating-wrapper">
                                    <span class="sc-c-range-bar__rating"
                                             ng-class="{'sc-c-range-bar__rating--pristine' : unset}">
                                             {{model}}
                                     </span>
                                    <i class="sc-c-range-bar__rating-icon icon-smiley-rating-{{model}}"></i>
                                </div>
                             </div>`;

            /**
             * Prefix for randomised ID on each directive

             * @private
             */
            this.prefix = 'c-review-rating-picker--';

            /**
             * Generate random ID
             *
             * @param {string} prefix BEMIT formatted identifier. e.g. 'c-component--'
             * @returns {string} Prefix + Random string
             */
            this.getRandomID = (prefix) => {
                return prefix + Math.random().toString(36).substr(2, 7);
            };
        }

        link = (scope: ng.IScope, element: ng.IAugmentedJQuery, attrs: ng.IAttributes, ngModel) => {
            const radix: number = 10;

            let min = parseInt(scope.min, radix),
                max = parseInt(scope.max, radix),
                step = parseInt(scope.step || 1, radix);

            //Used to apply a unique id to the element for ARIA purposes
            scope.componentId = this.getRandomID(this.prefix);

            scope.unset = !scope.model || scope.model === min;

            //basic checks of our min, max and step values
            if (!(min < max) || step === 0 || (max-min)%step !== 0) {
                throw 'Review Rating picker - Invalid parameters';
            }

            //used for creating our datalist, used in UI only need to start counting
            //at min + step
            scope.range = _.range(min+step, max+step, step);

            scope.click = {
                setValue: (value :number) => {
                    let firstValue = min+step;

                    scope.unset = false;

                    //this is to handle the fact that we start our display min+step and that there is no actual
                    //min block to select
                    if (value === firstValue && scope.model === firstValue) {
                        value = min;
                    }
                    ngModel.$setViewValue(value);
                }
            };

            let containerClasses = [];
            if(scope.size && scope.size.length > 0) {
                (<string[]>containerClasses).push(`sc-c-range-bar--${scope.size}`);
            }
            scope.containerClasses = containerClasses.join(' ');
        }

        static factory(): ng.IDirectiveFactory {
            const directive = () => new ScReviewRatingSelector();
            directive.$inject = [];
            return directive;
        }

    }

    const TR = angular.module('TR');
    TR.directive('scReviewRatingSelector', ScReviewRatingSelector.factory());

}
